'use client'

import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import {
  Fn,
  uniform,
  vec2,
  texture,
  float,
} from 'three/tsl'
import * as THREE from 'three'
import { createPerlinNoiseTexture } from './noiseTexture'

// Shared wind state — importable by Leaves and any other consumer
export const wind = {
  direction: uniform(new THREE.Vector2(Math.sin(0.6 * Math.PI), Math.cos(0.6 * Math.PI))),
  strength: uniform(0.5),
  localTime: uniform(0),
  positionFrequency: uniform(0.5),
  noiseTex: null,
  offsetNode: null,
}

// Build the offset node once the noise texture is ready
function ensureOffsetNode() {
  if (wind.offsetNode) return

  wind.noiseTex = createPerlinNoiseTexture()

  wind.offsetNode = Fn(([position]) => {
    const remappedPosition = position.mul(wind.positionFrequency)

    // Layer 1: faster, higher frequency
    const noiseUv1 = remappedPosition.xy.mul(0.2).add(wind.direction.mul(wind.localTime)).xy
    const noise1 = texture(wind.noiseTex, noiseUv1).r.sub(0.5)

    // Layer 2: slower, lower frequency
    const noiseUv2 = remappedPosition.xy.mul(0.1).add(wind.direction.mul(wind.localTime.mul(0.2))).xy
    const noise2 = texture(wind.noiseTex, noiseUv2).r.sub(0.5)

    const intensity = noise2.add(noise1)

    return vec2(wind.direction.mul(intensity).mul(wind.strength))
  })
}

// Hardcoded wind config (previously Leva controls)
const WIND_CONFIG = {
  angle: 0.6,       // x pi — the BASE direction the gusts swing around
  strength: 0.5,
  timeFrequency: 0.16,  // how fast the wind noise field scrolls (was 0.1 —
                        // too slow, so stagnation zones barely moved and
                        // debris piled onto them)
  gustSwing: 1.0,   // radians the direction swings either side of base
  gustSpeed: 0.09,  // base rad/s of the gust oscillation
}

export default function Wind() {
  const { angle, strength, timeFrequency, gustSwing, gustSpeed } = WIND_CONFIG
  const timeFreqRef = useRef(timeFrequency)
  const gustPhaseRef = useRef(0)

  // Ensure offset node is built; dispose on unmount
  useMemo(() => ensureOffsetNode(), [])
  useEffect(() => {
    return () => {
      if (wind.noiseTex) {
        wind.noiseTex.dispose()
        wind.noiseTex = null
      }
      wind.offsetNode = null
    }
  }, [])

  // Sync leva → uniforms
  useEffect(() => {
    wind.direction.value.set(
      Math.sin(angle * Math.PI),
      Math.cos(angle * Math.PI),
    )
  }, [angle])

  useEffect(() => {
    wind.strength.value = strength
  }, [strength])

  useEffect(() => {
    timeFreqRef.current = timeFrequency
  }, [timeFrequency])

  // Advance time + GUST each frame. The direction used to be constant, so
  // debris only ever blew one way and accumulated on the fixed downwind
  // stagnation line. Now the direction swings on a slow, non-repeating cycle
  // (two incommensurate sines) around the base angle, so the dead zones
  // wander and debris keeps skittering instead of lining up.
  useFrame((_, delta) => {
    wind.localTime.value += delta * timeFreqRef.current * wind.strength.value
    gustPhaseRef.current += delta * gustSpeed
    const p = gustPhaseRef.current
    const a = angle * Math.PI + (Math.sin(p) * 0.7 + Math.sin(p * 0.37 + 1.3) * 0.3) * gustSwing
    wind.direction.value.set(Math.sin(a), Math.cos(a))
  })

  return null
}
